import { useEffect, useRef, useState } from 'react'

// The Web Speech API has no TypeScript types in the DOM lib; this is the slice we use.
type Recognition = {
  lang: string
  interimResults: boolean
  continuous: boolean
  start: () => void
  stop: () => void
  abort: () => void
  onresult: ((e: { results: ArrayLike<ArrayLike<{ transcript: string }>> }) => void) | null
  onend: (() => void) | null
  onerror: ((e: { error?: string }) => void) | null
}
type RecognitionCtor = new () => Recognition

function recognitionCtor(): RecognitionCtor | undefined {
  if (typeof window === 'undefined') return undefined
  const w = window as unknown as { SpeechRecognition?: RecognitionCtor; webkitSpeechRecognition?: RecognitionCtor }
  return w.SpeechRecognition ?? w.webkitSpeechRecognition
}

/** True when this browser has speech recognition at all (the mic may still be blocked). */
export function hasSpeech(): boolean {
  return Boolean(recognitionCtor())
}

const DENIED = new Set(['not-allowed', 'service-not-allowed', 'audio-capture'])

export function joinSpoken(base: string, spoken: string): string {
  const s = spoken.trim()
  if (!s) return base
  return base ? `${base} ${s}` : s.charAt(0).toUpperCase() + s.slice(1)
}

/**
 * Browser dictation (en-US, continuous, interim results). `start(base, onText)` streams base + everything said so far
 * into onText as it is heard. `denied` flips when the mic is blocked, so callers can fall back to typing.
 */
export function useDictation() {
  const [Ctor] = useState(recognitionCtor)
  const [listening, setListening] = useState(false)
  const [denied, setDenied] = useState(false)
  const rec = useRef<Recognition | null>(null)
  useEffect(() => () => rec.current?.abort(), [])

  const start = (base: string, onText: (text: string) => void) => {
    if (!Ctor || rec.current) return
    const r = new Ctor()
    r.lang = 'en-US'
    r.interimResults = true
    r.continuous = true
    const head = base.trimEnd()
    r.onresult = (e) => {
      let spoken = ''
      for (let i = 0; i < e.results.length; i++) spoken += e.results[i][0]?.transcript ?? ''
      onText(joinSpoken(head, spoken))
    }
    r.onend = () => {
      rec.current = null
      setListening(false)
    }
    r.onerror = (e) => {
      if (e?.error && DENIED.has(e.error)) setDenied(true)
      rec.current = null
      setListening(false)
    }
    try {
      r.start()
      rec.current = r
      setListening(true)
    } catch {
      setListening(false)
    }
  }

  const stop = () => rec.current?.stop()

  return { supported: Boolean(Ctor), listening, denied, start, stop }
}
